import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import Icon from '../../../components/AppIcon';
import { astraAPI } from '../../../services/astraApi';

/**
 * P&L Chart Component
 *
 * 🔧 PRODUCTION VERSION (2025-10-27): Real Time Series Data
 *
 * Issues Fixed:
 * 1. Removed 100% hardcoded mockData (Lines 10-29 in old version)
 * 2. Integrated with /portfolio/performance/timeseries API
 * 3. Added loading and error states
 * 4. Dynamic timeframe switching
 * 5. Real realized vs unrealized P&L tracking
 */

const PLChart = () => {
  const [timeframe, setTimeframe] = useState('daily');
  const [chartType, setChartType] = useState('line');
  const [plData, setPlData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPLData();
  }, [timeframe]);

  const fetchPLData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Determine days based on timeframe
      const daysMap = {
        'daily': 30,
        'weekly': 90,
        'monthly': 365
      };

      const days = daysMap[timeframe] || 30;

      // Fetch real time series data from API
      const data = await astraAPI.getPerformanceTimeSeries(1453, timeframe, days);

      console.log('📊 P&L Time Series Data:', {
        timeframe,
        points: data.length,
        sample: data.slice(0, 3)
      });

      // Transform API data to chart format
      const chartData = data.map(point => ({
        date: point.date,
        realized: point.realized_pnl,
        unrealized: point.unrealized_pnl,
        total: point.total_pnl
      }));

      setPlData(chartData);

    } catch (err) {
      console.error('Failed to fetch P&L data:', err);
      setError('Failed to load P&L data');
      setPlData([]);
    } finally {
      setLoading(false);
    }
  };

  const timeframes = [
    { key: 'daily', label: 'Daily', icon: 'Calendar' },
    { key: 'weekly', label: 'Weekly', icon: 'CalendarDays' },
    { key: 'monthly', label: 'Monthly', icon: 'CalendarRange' }
  ];

  const chartTypes = [
    { key: 'line', label: 'Line', icon: 'TrendingUp' },
    { key: 'area', label: 'Area', icon: 'AreaChart' }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-elevated">
          <p className="text-sm font-medium text-popover-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center justify-between space-x-4 text-sm">
              <div className="flex items-center space-x-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: entry?.color }}
                ></div>
                <span className="text-muted-foreground capitalize">{entry?.dataKey}</span>
              </div>
              <span className="font-medium text-popover-foreground">
                ₹{entry?.value?.toLocaleString('en-IN')}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const renderChart = () => {
    if (chartType === 'line') {
      return (
        <LineChart data={plData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
          <XAxis
            dataKey="date"
            stroke="var(--color-muted-foreground)"
            fontSize={12}
          />
          <YAxis
            stroke="var(--color-muted-foreground)"
            fontSize={12}
            tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}K`}
          />
          <Tooltip content={<CustomTooltip />} />
          <Line
            type="monotone"
            dataKey="realized"
            stroke="#10B981"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 6 }}
          />
          <Line
            type="monotone"
            dataKey="unrealized"
            stroke="#3B82F6"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 6 }}
          />
          <Line
            type="monotone"
            dataKey="total"
            stroke="#8B5CF6"
            strokeWidth={3}
            dot={false}
            activeDot={{ r: 8 }}
          />
        </LineChart>
      );
    } else {
      return (
        <AreaChart data={plData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
          <XAxis
            dataKey="date"
            stroke="var(--color-muted-foreground)"
            fontSize={12}
          />
          <YAxis
            stroke="var(--color-muted-foreground)"
            fontSize={12}
            tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}K`}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone"
            dataKey="total"
            stackId="1"
            stroke="#8B5CF6"
            fill="#8B5CF6"
            fillOpacity={0.3}
          />
          <Area
            type="monotone"
            dataKey="realized"
            stackId="2"
            stroke="#10B981"
            fill="#10B981"
            fillOpacity={0.6}
          />
          <Area
            type="monotone"
            dataKey="unrealized"
            stackId="3"
            stroke="#3B82F6"
            fill="#3B82F6"
            fillOpacity={0.6}
          />
        </AreaChart>
      );
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="TrendingUp" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">P&L Performance</h3>
            <p className="text-sm text-muted-foreground">Realized vs Unrealized gains</p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {/* Chart Type Toggle */}
          <div className="flex bg-muted rounded-lg p-1">
            {chartTypes?.map((type) => (
              <button
                key={type?.key}
                onClick={() => setChartType(type?.key)}
                className={`flex items-center space-x-1 px-3 py-1 rounded-md text-sm font-medium transition-smooth ${
                  chartType === type?.key
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={type?.icon} size={14} />
                <span>{type?.label}</span>
              </button>
            ))}
          </div>

          {/* Timeframe Toggle */}
          <div className="flex bg-muted rounded-lg p-1">
            {timeframes?.map((tf) => (
              <button
                key={tf?.key}
                onClick={() => setTimeframe(tf?.key)}
                className={`flex items-center space-x-1 px-3 py-1 rounded-md text-sm font-medium transition-smooth ${
                  timeframe === tf?.key
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tf?.icon} size={14} />
                <span>{tf?.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Chart Container */}
      {loading ? (
        <div className="h-80 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
            <p className="text-sm text-muted-foreground">Loading chart data...</p>
          </div>
        </div>
      ) : error ? (
        <div className="h-80 flex items-center justify-center">
          <div className="text-center">
            <Icon name="AlertTriangle" size={24} className="text-error mx-auto mb-2" />
            <p className="text-sm text-muted-foreground mb-3">{error}</p>
            <button
              onClick={fetchPLData}
              className="px-3 py-1 text-xs bg-primary text-primary-foreground rounded hover:bg-primary/90"
            >
              Retry
            </button>
          </div>
        </div>
      ) : plData.length === 0 ? (
        <div className="h-80 flex items-center justify-center">
          <div className="text-center">
            <Icon name="BarChart3" size={24} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No P&L data available</p>
          </div>
        </div>
      ) : (
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            {renderChart()}
          </ResponsiveContainer>
        </div>
      )}

      {/* Legend */}
      <div className="mt-6 flex items-center justify-center space-x-6">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
          <span className="text-sm text-muted-foreground">Realized P&L</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 rounded-full bg-blue-500"></div>
          <span className="text-sm text-muted-foreground">Unrealized P&L</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 rounded-full bg-purple-500"></div>
          <span className="text-sm text-muted-foreground">Total P&L</span>
        </div>
      </div>
    </div>
  );
};

export default PLChart;
